import { useState } from 'react';
import { ChevronDown, Copy, Download, Eye } from 'lucide-react';
import ExportFormatModal from './ExportFormatModal';
import { downloadBlob, convertContent } from '../../utils/documentExport';

/**
 * Version Comparison Component - Client Portal Version
 * Shows side-by-side or line-by-line diff between two versions (read-only)
 */
export default function VersionComparison({ versions }) {
  const [compareMode, setCompareMode] = useState('side-by-side');
  const [version1Index, setVersion1Index] = useState(Math.max(0, versions.length - 2));
  const [version2Index, setVersion2Index] = useState(versions.length - 1);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  if (!versions || versions.length < 2) {
    return (
      <div className="text-center py-8">
        <p className="text-secondary-500">At least 2 versions needed to compare</p>
      </div>
    );
  }

  const version1 = versions[version1Index];
  const version2 = versions[version2Index];

  const parseContent = (content) => {
    if (!content) return [];
    return content.split('\n');
  };

  const getDiff = () => {
    const v1Lines = parseContent(version1.content || version1.fileUrl);
    const v2Lines = parseContent(version2.content || version2.fileUrl);
    
    return {
      added: v2Lines.filter((line) => !v1Lines.includes(line)),
      removed: v1Lines.filter((line) => !v2Lines.includes(line)),
      unchanged: v1Lines.filter((line) => v2Lines.includes(line)),
    };
  };

  const handleExportDiff = (format) => {
    try {
      setIsExporting(true);
      const diffHtml = `
        <h2>Version Comparison</h2>
        <h3>Version ${version1Index + 1} (${version1.timestamp})</h3>
        ${version1.content || ''}
        <h3>Version ${version2Index + 1} (${version2.timestamp})</h3>
        ${version2.content || ''}
      `;
      const { blob, extension } = convertContent(diffHtml, format);
      const fileName = `version-comparison${extension}`;
      downloadBlob(blob, fileName);
      setShowExportModal(false);
    } catch (error) {
      console.error('Export diff failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const buildDiffText = () => {
    const diff = getDiff();
    const removedText = diff.removed.map((l) => `- ${l}`).join('\n');
    const addedText = diff.added.map((l) => `+ ${l}`).join('\n');
    return [
      `Version ${version1.versionNumber} (${new Date(version1.uploadedAt).toLocaleString()})`,
      removedText,
      '',
      `Version ${version2.versionNumber} (${new Date(version2.uploadedAt).toLocaleString()})`,
      addedText,
    ].join('\n');
  };

  const handleCopyDiff = async () => {
    try {
      await navigator.clipboard.writeText(buildDiffText());
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  const handleViewFull = () => {
    // Open window immediately (synchronously) to avoid popup blocker
    const w = window.open('', '_blank');
    if (!w) {
      alert('Unable to open new window - check popup settings');
      return;
    }

    // Write loading message while fetching
    w.document.write('<html><body><p>Loading...</p></body></html>');

    try {
      let html = version2.content || '';
      
      // If content is a URL, fetch it
      if (!html && version2.fileUrl) {
        fetch(version2.fileUrl)
          .then(response => response.ok ? response.text() : Promise.reject('Failed to fetch'))
          .then(content => {
            w.document.open();
            w.document.write(content);
            w.document.close();
          })
          .catch(error => {
            w.document.open();
            w.document.write(`<html><body><p>Error loading content: ${error}</p></body></html>`);
            w.document.close();
          });
        return;
      }
      
      if (!html && version2.url) {
        fetch(version2.url)
          .then(response => response.ok ? response.text() : Promise.reject('Failed to fetch'))
          .then(content => {
            w.document.open();
            w.document.write(content);
            w.document.close();
          })
          .catch(error => {
            w.document.open();
            w.document.write(`<html><body><p>Error loading content: ${error}</p></body></html>`);
            w.document.close();
          });
        return;
      }

      if (!html) {
        w.document.open();
        w.document.write('<html><body><p>No content available to view</p></body></html>');
        w.document.close();
        return;
      }

      w.document.write(html);
      w.document.close();
    } catch (error) {
      console.error('Failed to view full version:', error);
      w.document.open();
      w.document.write(`<html><body><p>Error: ${error.message}</p></body></html>`);
      w.document.close();
    }
  };

  const diff = getDiff();
  const totalChanges = diff.added.length + diff.removed.length;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
        <div className="space-y-4">
          {/* Mode selector */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-secondary-900">View Mode:</span>
            <div className="flex gap-2">
              {['side-by-side', 'inline'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setCompareMode(mode)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    compareMode === mode
                      ? 'bg-primary-600 text-white'
                      : 'bg-white border border-secondary-200 text-secondary-700 hover:border-secondary-300'
                  }`}
                >
                  {mode === 'side-by-side' ? 'Side-by-Side' : 'Inline'}
                </button>
              ))}
            </div>
          </div>

          {/* Version selectors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-900 mb-2">
                Version 1 (Original)
              </label>
              <div className="relative">
                <select
                  value={version1Index}
                  onChange={(e) => setVersion1Index(parseInt(e.target.value))}
                  className="input appearance-none"
                >
                  {versions.map((v, idx) => (
                    <option key={v._id} value={idx}>
                      Version {v.versionNumber} - {new Date(v.uploadedAt).toLocaleDateString()}
                    </option>
                  ))}
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-secondary-500" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-900 mb-2">
                Version 2 (New)
              </label>
              <div className="relative">
                <select
                  value={version2Index}
                  onChange={(e) => setVersion2Index(parseInt(e.target.value))}
                  className="input appearance-none"
                >
                  {versions.map((v, idx) => (
                    <option key={v._id} value={idx}>
                      Version {v.versionNumber} - {new Date(v.uploadedAt).toLocaleDateString()}
                    </option>
                  ))}
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-secondary-500" />
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="flex gap-4 pt-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-sm"></div>
              <span className="text-sm text-secondary-700">
                {diff.removed.length} removed
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
              <span className="text-sm text-secondary-700">
                {diff.added.length} added
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gray-500 rounded-sm"></div>
              <span className="text-sm text-secondary-700">
                {diff.unchanged.length} unchanged
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Diff Viewer */}
      {totalChanges === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
          <p className="text-green-800 font-medium">No differences found between these versions</p>
        </div>
      ) : compareMode === 'side-by-side' ? (
        <div className="border border-secondary-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-2 divide-x divide-secondary-200 max-h-96 overflow-y-auto">
            {/* Version 1 */}
            <div className="bg-white">
              <div className="sticky top-0 bg-secondary-100 p-3 border-b border-secondary-200">
                <h4 className="font-semibold text-secondary-900 text-sm">
                  Version {version1.versionNumber}
                </h4>
              </div>
              <div className="p-3 font-mono text-xs space-y-1 bg-red-50">
                {diff.removed.map((line, idx) => (
                  <div key={idx} className="bg-red-100 text-red-900 p-1 rounded border-l-2 border-red-500 line-clamp-2">
                    {line}
                  </div>
                ))}
              </div>
            </div>

            {/* Version 2 */}
            <div className="bg-white">
              <div className="sticky top-0 bg-secondary-100 p-3 border-b border-secondary-200">
                <h4 className="font-semibold text-secondary-900 text-sm">
                  Version {version2.versionNumber}
                </h4>
              </div>
              <div className="p-3 font-mono text-xs space-y-1 bg-green-50">
                {diff.added.map((line, idx) => (
                  <div key={idx} className="bg-green-100 text-green-900 p-1 rounded border-l-2 border-green-500 line-clamp-2">
                    {line}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-secondary-200 rounded-lg overflow-hidden bg-white max-h-96 overflow-y-auto">
          <div className="p-4 font-mono text-xs space-y-0">
            {/* Show removed lines */}
            {diff.removed.length > 0 && (
              <div>
                <div className="sticky top-0 bg-red-100 text-red-900 font-semibold p-2 border-l-4 border-red-600">
                  Removed ({diff.removed.length})
                </div>
                {diff.removed.slice(0, 5).map((line, idx) => (
                  <div key={idx} className="bg-red-50 text-red-800 p-2 border-l-4 border-red-500 line-clamp-2">
                    - {line}
                  </div>
                ))}
                {diff.removed.length > 5 && (
                  <div className="p-2 text-secondary-500 italic">
                    ... and {diff.removed.length - 5} more removed lines
                  </div>
                )}
              </div>
            )}

            {/* Show added lines */}
            {diff.added.length > 0 && (
              <div className="mt-2">
                <div className="sticky top-0 bg-green-100 text-green-900 font-semibold p-2 border-l-4 border-green-600">
                  Added ({diff.added.length})
                </div>
                {diff.added.slice(0, 5).map((line, idx) => (
                  <div key={idx} className="bg-green-50 text-green-800 p-2 border-l-4 border-green-500 line-clamp-2">
                    + {line}
                  </div>
                ))}
                {diff.added.length > 5 && (
                  <div className="p-2 text-secondary-500 italic">
                    ... and {diff.added.length - 5} more added lines
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          className="btn-secondary flex items-center gap-2"
          onClick={handleCopyDiff}
        >
          <Copy size={18} />
          Copy Diff
        </button>
        <button 
          className="btn-secondary flex items-center gap-2"
          onClick={() => setShowExportModal(true)}
          disabled={isExporting}
        >
          <Download size={18} />
          Export Diff
        </button>
        <button className="btn-secondary flex items-center gap-2" onClick={handleViewFull}>
          <Eye size={18} />
          View Full Version
        </button>
      </div>

      <ExportFormatModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onSelect={handleExportDiff}
        title="Export Diff Format"
        isLoading={isExporting}
      />
    </div>
  );
}
